import { Badge, Button, Form, Modal } from 'react-bootstrap';
import { BeaconFunctionsRow } from '../../../common/beacon.interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import XSvg from '../../svg/X-Svg';
import ArrowRightSvg from '../../svg/ArrowRightSvg';
import '../../../styles/beacon.css'

interface BeaconEditFunctionModal {
  visible: boolean;
  func: BeaconFunctionsRow;
  closeCallback: any;
  saveCallback: any;
  functionApp: string;
  functionName: string;
  tags: string[];
}

export default function BeaconEditFunctionModal(props: BeaconEditFunctionModal) {
  const [clone, setClone] = useState<BeaconFunctionsRow | undefined>(undefined);
  const [tagText, setTagText] = useState<string | undefined>(undefined);
  const [newTag, setNewTag] = useState<string>('');
  const [timerTimeInMinutes, setTimerTimeInMinutes] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!props.func) return;
    setClone(JSON.parse(JSON.stringify(props.func)));
    if (props.func?.functionTrigger === 'timer' && props.func?.timerTimeInMinutes) {
      setTimerTimeInMinutes(props.func?.timerTimeInMinutes);
    }
  }, [props.func]);

  const handleClose = useCallback(() => {
    setClone(undefined);
    setTagText(undefined);
    setNewTag('');
    setTimerTimeInMinutes(undefined);
    props.closeCallback();
  }, []);

  const handleSave = useCallback(() => {
    if (!clone?.tags) return;

    if (clone.functionTrigger === 'timer' && timerTimeInMinutes === undefined) {
      return;
    }

    props.saveCallback({
      ...props.func,
      ...clone,
      timerTimeInMinutes,
    });
    setClone(undefined);
    setTagText(undefined);
    setNewTag('');
    setTimerTimeInMinutes(undefined);
  }, [props.func, clone, timerTimeInMinutes]);

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTag(e.target.value.trim().toUpperCase());
  };

  const addTag = () => {
    if (!newTag || newTag === '-- SELECT A TAG --') return;
    const tags = JSON.parse(clone?.tags ?? '[]');
    if (!tags.includes(newTag)) {
      const updatedTags = [...tags, newTag];
      setClone({ ...clone, tags: JSON.stringify(updatedTags) });
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    const tags = JSON.parse(clone?.tags ?? '[]').filter((t: string) => t !== tag);
    setClone({ ...clone, tags: JSON.stringify(tags) });
  };

  const handleTagSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewTag(e.target.value);
  };

  const handleTriggerTypeSelected = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setClone(prev => prev ? { ...prev, functionTrigger: e.target.value } as BeaconFunctionsRow : prev);
  }, []);

  const handleTimerTimeChanged = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    if (!isNaN(Number(value)) && value.length > 0) {
      setTimerTimeInMinutes(Number(value));
    }
  }, []);

  const view = useMemo(() => {
    const tags = JSON.parse(clone?.tags ?? '[]');
    return (
      <Modal show={props.visible} animation={false} onHide={handleClose} className='modal-xl'>
        <Modal.Header closeButton>
          <Modal.Title><span className='beacon-header'>Function Settings for {props.functionApp} <ArrowRightSvg height={15}/> {props.functionName}</span></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Function Trigger */}
          <span className='beacon-header'>TRIGGER</span>
          <Form.Select style={{width: '200px'}} onChange={handleTriggerTypeSelected} value={clone?.functionTrigger ?? props.func?.functionTrigger}>
            <option value=''>-- SELECT A TRIGGER TYPE --</option>
            <option value='timer'>Timer Trigger</option>
            <option value='http'>HTTP Trigger</option>
            <option value='blob'>Blob Trigger</option>
          </Form.Select>
          
          {/* Timer Settings */}
          {clone?.functionTrigger === 'timer' && (
            <>
              <Form.Control
                type='number'
                placeholder='Enter time in minutes...'
                value={timerTimeInMinutes}
                onChange={handleTimerTimeChanged}
                style={{width: '200px'}}
              />
              {timerTimeInMinutes !== undefined && timerTimeInMinutes > 0 ? (
                <span className='beacon-label'>Function runs every {timerTimeInMinutes} minute(s).</span>
              ) : (
                <span className='text-danger'>Invalid timer settings. Please enter a valid number.</span>
              )}
            </>
          )}

          {/* Tags */}
          <div className="d-flex flex-wrap gap-2 mt-2">
            {tags.map((t: string) => (
              <Badge bg="secondary" key={t} className="d-flex align-items-center gap-2 p-2">
                {t}
                <Button variant="outline-light" size="sm" className="p-0 border-0" onClick={() => removeTag(t)}>
                  <XSvg height={15} />
                </Button>
              </Badge>
            ))}
          </div>

          <br />
          <span className='beacon-header'>TAGS</span>
          <Form.Control type='text' value={newTag} onChange={handleTagInputChange} style={{width: '200px'}} placeholder='Enter a new tag...' />
          <Button variant='secondary' className='mt-2' onClick={addTag}>Add Tag</Button>

          {props.tags?.length > 0 && (
            <div>
              <span className='beacon-label'>or select a tag</span>
              <Form.Select onChange={handleTagSelectChange} value={newTag}  style={{width: '200px'}}>
                <option>-- SELECT A TAG --</option>
                {props.tags?.sort()?.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </Form.Select>
              <Button variant='secondary' className='mt-2' onClick={addTag}>Add Selected Tag</Button>
            </div>
          )}
          <span>{tagText}</span>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-start">
          <Button variant="success" onClick={handleSave}>SAVE</Button>
        </Modal.Footer>
      </Modal>
    );
  }, [props, clone, newTag, tagText, timerTimeInMinutes]);

  return view;
}


// import { Badge, Button, Form, Modal } from 'react-bootstrap';
// import { BeaconFunctionsRow } from './beacon.interfaces';
// import { useCallback, useEffect, useMemo, useState } from 'react';

// interface BeaconEditFunctionModal {
//   visible: boolean;
//   func: BeaconFunctionsRow;
//   closeCallback: () => void;
//   saveCallback: (updatedFunc: BeaconFunctionsRow) => void;
//   title: string;
//   tags: string[];
// }

// export default function BeaconEditFunctionModal(props: BeaconEditFunctionModal) {
//   const [clone, setClone] = useState<BeaconFunctionsRow | undefined>(undefined);
//   const [tagText, setTagText] = useState<string | undefined>(undefined);

//   useEffect(() => {
//     if (!props.func) return;
//     setClone(JSON.parse(JSON.stringify(props.func)));
//   }, [props.func]);

//   const handleTriggerTypeSelected = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
//     if (e.target.value === '-- SELECT A TRIGGER TYPE --') return;
//     setClone(prev => prev ? { ...prev, functionTrigger: e.target.value } as BeaconFunctionsRow : prev);
//   }, []);

//   const handleTimerTimeChanged = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
//     const value = e.target.value.trim();
//     if (!isNaN(Number(value)) && value.length > 0) {
//       setClone(prev => prev ? { ...prev, timerTimeInMinutes: Number(value) } as BeaconFunctionsRow : prev);
//     }
//   }, []);

//   const handleClose = useCallback(() => {
//     setClone(undefined);
//     props.closeCallback();
//   }, []);

//   const handleSave = useCallback(() => {
//     if (!clone?.functionTrigger) return;
//     if (clone.functionTrigger === 'timer' && typeof clone.timerTimeInMinutes !== 'number') return;
//     props.saveCallback({ ...props.func, ...clone });
//     setClone(undefined);
//   }, [props.func, clone]);

//   const handleTagsChanged = useCallback((e) => {
//     const value = e.target.value.trim().toUpperCase();
//     if (!value || value === '-- SELECT A TAG --') {
//       setTagText(undefined);
//       return;
//     }

//     setTagText(props.tags.includes(value) ? `"${value}" exists and will be grouped there` : `"${value}" will be added as a new tag`);
//     setClone(prev => {
//       if (!prev) return prev;
//       const funcTags: string[] = JSON.parse(prev.tags ?? '[]');
//       if (!funcTags.includes(value)) {
//         return { ...prev, tags: JSON.stringify([...funcTags, value]) } as BeaconFunctionsRow;
//       }
//       return prev;
//     });
//   }, [props.tags]);

//   const removeTag = (tagToRemove: string) => {
//     setClone(prev => {
//       if (!prev) return prev;
//       const updatedTags = JSON.parse(prev.tags ?? '[]').filter((t: string) => t !== tagToRemove);
//       return { ...prev, tags: JSON.stringify(updatedTags) } as BeaconFunctionsRow;
//     });
//   };

//   const view = useMemo(() => {
//     const tags = JSON.parse(clone?.tags ?? '[]');
//     return (
//       <Modal show={props.visible} animation={false} onHide={handleClose} className='modal-xl'>
//         <Modal.Header closeButton>
//           <Modal.Title>{props.title}</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form.Select onChange={handleTriggerTypeSelected} defaultValue={clone?.functionTrigger ?? props.func?.functionTrigger}>
//             <option value=''>-- SELECT A TRIGGER TYPE --</option>
//             <option value='timer'>Timer Trigger</option>
//             <option value='http'>HTTP Trigger</option>
//             <option value='blob'>Blob Trigger</option>
//           </Form.Select>
//           {clone?.functionTrigger === 'timer' && (
//             <>
//               <Form.Control type='number' placeholder='Enter time in minutes...' defaultValue={clone.timerTimeInMinutes} onChange={handleTimerTimeChanged} />
//               {typeof clone.timerTimeInMinutes !== 'number' && <span className='text-danger'>Invalid timer settings. Please enter a valid number.</span>}
//               {typeof clone.timerTimeInMinutes === 'number' && <span>Function runs every {clone.timerTimeInMinutes} minute(s)</span>}
//             </>
//           )}
//           <div className='d-flex flex-wrap gap-2 mt-2'>
//             {tags.map(t => (
//               <Badge key={t} bg='secondary' className='d-flex align-items-center gap-2 p-2'>
//                 {t}
//                 <Button variant='outline-light' size='sm' className='p-0 border-0' onClick={() => removeTag(t)}>
//                   x
//                 </Button>
//               </Badge>
//             ))}
//           </div>
//           <Form.Label className='mt-3'>Add a tag</Form.Label>
//           <Form.Control type='text' name='funcTags' autoComplete='on' onChange={handleTagsChanged} />
//           {props.tags?.length > 0 && (
//             <>
//               <Form.Label>Or select a tag</Form.Label>
//               <Form.Select onChange={handleTagsChanged}>
//                 <option>-- SELECT A TAG --</option>
//                 {props.tags?.sort()?.map(tag => (
//                   <option key={tag} value={tag}>{tag}</option>
//                 ))}
//               </Form.Select>
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant='danger' onClick={handleClose}>Close</Button>
//           <Button variant='success' onClick={handleSave}>Save</Button>
//         </Modal.Footer>
//       </Modal>
//     );
//   }, [props, clone, tagText]);

//   return view;
// }
